import React, { useState, useEffect } from 'react';
import Axios from "axios";
import { 
  Grid, Typography, CircularProgress, Container, Box, 
  Button, Card, CardActionArea, Avatar, TextField, MenuItem,
  FormControlLabel, Checkbox, Chip, Tabs, Tab, FormControl, InputLabel,
  ToggleButton, ToggleButtonGroup, List, ListItem, ListItemAvatar, ListItemText, Divider
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";
import { MasterCategories } from "../../Constants/BusinessOptions";
import SpeedIcon from '@mui/icons-material/Speed';
import ConstructionIcon from '@mui/icons-material/Construction';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import config, { environment } from '../../config';

function Masters() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedGroups, setSelectedGroups] = useState({});
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [bringsToolsOnly, setBringsToolsOnly] = useState(false);
  const [tabValue, setTabValue] = useState(1);
  
  // Set tab to 0 (Registered) if there are no recommended masters but there are registered ones
  useEffect(() => {
    if (!loading && masters.length > 0) {
      const hasRegistered = masters.some(m => !(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True'));
      const hasRecommended = masters.some(m => !!(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True'));
      
      if (!hasRecommended && hasRegistered && tabValue === 1) {
        setTabValue(0);
      }
    }
  }, [loading, masters, tabValue]);
  const [viewMode, setViewMode] = useState('card');
  
  // Location filters
  const [szekFilter, setSzekFilter] = useState('');
  const [kozsegFilter, setKozsegFilter] = useState('');
  const [szekOptions, setSzekOptions] = useState([]);
  const [kozsegOptions, setKozsegOptions] = useState([]);
  const [isCsango, setIsCsango] = useState(false);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchMasters() {
      try {
        const response = await Axios.get(`${config[environment].businessesEndpoint}?type=Master`, { cancelToken: source.token });
        setMasters(response.data);
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          setLoading(false);
        }
      }
    }
    
    async function fetchSzekek() {
      try {
        if (isCsango) {
          setSzekOptions([{ id: 'csango', nev: 'Csángóföld' }]);
        } else {
          const response = await Axios.get(`${config[environment].apiEndpoint}api/szek/`);
          const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
          setSzekOptions(data);
        }
      } catch (e) {
      }
    }

    fetchMasters();
    fetchSzekek();
    return () => source.cancel();
  }, [isCsango]);

  useEffect(() => {
    if (szekFilter || isCsango) {
      const fetchKozsegek = async () => {
        try {
          if (isCsango) {
            const fetchAllKozsegek = async (page = 1, accumulated = []) => {
              const response = await Axios.get(config[environment].csangoKozsegVarosEndpoint(page));
              let data = [];
              if (response.data.features) {
                data = response.data.features.map(f => ({
                  id: f.properties.Id || f.properties.id || f.id,
                  nev: f.properties.név || f.properties.nev || f.properties.name || f.properties.nev_hu
                }));
              } else {
                const results = response.data.results || response.data || [];
                data = (Array.isArray(results) ? results : []).map(item => ({
                  id: item.id || item.uniqueid || item.pk,
                  nev: item.nev || item.name || item.nev_hu
                }));
              }
              const combined = [...accumulated, ...data];
              if (response.data.next) {
                return fetchAllKozsegek(page + 1, combined);
              }
              return combined;
            };
            const kozsegData = await fetchAllKozsegek();
            const uniqueKozsegek = Array.from(new Map(kozsegData.map(item => [item.id || item.Id || item.uniqueid || item.pk, item])).values());
            setKozsegOptions(uniqueKozsegek.sort((a, b) => (a.nev || '').localeCompare(b.nev || '')));
          } else {
            // Megkeressük a szék objektumot a név kinyeréséhez, ami megbízhatóbb a dropdown végpontnál
            const selectedSzekObj = szekOptions.find(s => s.nev === szekFilter || s.name === szekFilter || String(s.id) === String(szekFilter));
            const szekName = selectedSzekObj ? (selectedSzekObj.nev || selectedSzekObj.name) : szekFilter;

            const response = await Axios.get(`${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?szek_nev=${encodeURIComponent(szekName)}`);
            const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
            setKozsegOptions(data);
          }
        } catch (e) {
        }
      };
      fetchKozsegek();
    } else {
      setKozsegOptions([]);
    }
  }, [szekFilter, isCsango, config, environment, szekOptions]);

  const categoryGroups = [...new Set(MasterCategories.map(cat => cat.group))]
    .sort((a, b) => t(a, a).localeCompare(t(b, b), i18n.language));

  const handleGroupChange = (group, value) => {
    const newSelectedGroups = { ...selectedGroups, [group]: value };
    
    // Reset other groups if a value is selected in this one
    if (value) {
      Object.keys(newSelectedGroups).forEach(g => {
        if (g !== group) newSelectedGroups[g] = '';
      });
    }
    
    setSelectedGroups(newSelectedGroups);
    setCategoryFilter(value);
  };

  const handleSzekChange = (value) => {
    setSzekFilter(value);
    setKozsegFilter('');
    if (value === 'csango' || value === 'Csángóföld') {
      setIsCsango(true);
    } else {
      setIsCsango(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const filteredMasters = masters.filter(m => {
    const matchesCategory = categoryFilter
      ? (m.category && m.category.toLowerCase().includes(categoryFilter.toLowerCase())) ||
        (m.description && m.description.toLowerCase().includes(categoryFilter.toLowerCase()))
      : true;
    const matchesUrgent = urgentOnly ? m.is_urgent : true;
    const matchesTools = bringsToolsOnly ? m.brings_tools : true;
    
    const matchesSzek = szekFilter ? (m.szek && (m.szek.toLowerCase().includes(szekFilter.toLowerCase()) || m.szek.toLowerCase() === szekFilter.toLowerCase())) : true;
    const matchesKozseg = kozsegFilter ? (m.kozsegvaros && (m.kozsegvaros.toLowerCase().includes(kozsegFilter.toLowerCase()) || m.kozsegvaros.toLowerCase() === kozsegFilter.toLowerCase())) : true;

    // Filter by recommendation status
    // Safe check for is_recommended as it might be missing in data structure
    const isRec = !!(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True' || m.is_recommended === 'true' || m.is_recommended === true);
    
    const matchesTab = tabValue === 1 ? isRec : !isRec;
    const finalResult = matchesCategory && matchesUrgent && matchesTools && matchesTab && matchesSzek && matchesKozseg;
    
    return finalResult;
  });

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
            {t('SubMenu.Masters')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate(`/${i18n.language}/mester-ajanlasa`)}
              sx={{ color: 'var(--sznodarkblue)', borderColor: 'var(--sznodarkblue)' }}
              fullWidth
            >
              {t('Business.RecommendMaster')}
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/${i18n.language}/uzleti-regisztracio`)}
              sx={{ bgcolor: 'var(--sznodarkblue)' }}
              fullWidth
            >
              {t('Business.RegisterAsMaster', 'Jelentkezés mesternek')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="master tabs">
            <Tab label={`${t('Business.Registered')} (${masters.filter(m => !(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True')).length})`} sx={{ fontWeight: 'bold' }} />
            <Tab 
              label={`${t('Business.RecommendedMasters')} (${masters.filter(m => !!(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True')).length})`}
              sx={{ 
                fontWeight: 'bold',
                color: masters.some(m => !!(m.is_recommended === true || m.is_recommended === 1 || m.is_recommended === '1' || m.is_recommended === 'true' || m.is_recommended === 'True')) ? 'var(--sznored)' : 'inherit'
              }} 
            />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <TextField
            select
            label={t('Events.Szek')}
            value={szekFilter}
            onChange={(e) => handleSzekChange(e.target.value)}
            sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(25% - 16px)' } }}
            size="small"
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            <MenuItem value="">{t('Common.All')}</MenuItem>
            {szekOptions.map((sz, index) => (
              <MenuItem key={`${sz.id || sz.uniqueid || sz.pk || 'szek'}-${index}`} value={sz.nev || sz.name}>
                {sz.nev || sz.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('Events.Kozseg')}
            value={kozsegFilter}
            onChange={(e) => setKozsegFilter(e.target.value)}
            sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(25% - 16px)' } }}
            size="small"
            disabled={!szekFilter && !isCsango}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            <MenuItem value="">{t('Common.All')}</MenuItem>
            {kozsegOptions.map((k, index) => (
              <MenuItem key={`${k.id || k.uniqueid || k.pk || 'kozseg'}-${index}`} value={k.nev || k.name}>
                {k.nev || k.name}
              </MenuItem>
            ))}
          </TextField>

          {categoryGroups.map(group => (
            <TextField
              key={group}
              select
              label={group.startsWith('Business.') ? t(group, group) : group}
              value={selectedGroups[group] || ''}
              onChange={(e) => handleGroupChange(group, e.target.value)}
              sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(25% - 16px)' } }}
              size="small"
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
            >
              <MenuItem value="">{t('Common.All')}</MenuItem>
              {MasterCategories
                .filter(cat => cat.group === group)
                .map(cat => ({ ...cat, translatedLabel: cat.label.startsWith('Business.') ? t(cat.label, cat.label) : cat.label }))
                .sort((a, b) => a.translatedLabel.localeCompare(b.translatedLabel, i18n.language))
                .map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.translatedLabel}
                  </MenuItem>
                ))}
            </TextField>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: 'bold' }}>{t('Common.Filters', 'Szűrők')}:</Typography>
            <FormControlLabel
              control={<Checkbox checked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)} color="error" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon sx={{ mr: 0.5, fontSize: 20, color: '#d32f2f' }} />
                  <Typography variant="body2">{t('Business.UrgentNow', 'Sürgős most')}</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={<Checkbox checked={bringsToolsOnly} onChange={(e) => setBringsToolsOnly(e.target.checked)} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ConstructionIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  <Typography variant="body2">{t('Business.BringsTools', 'Hoz szerszámot')}</Typography>
                </Box>
              }
            />
          </Box>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
            sx={{ bgcolor: 'white' }}
          >
            <ToggleButton value="card" aria-label="card view">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {filteredMasters.length === 0 ? (
        <Typography variant="h6" sx={{ mt: 4, width: '100%', textAlign: 'center' }}>
          {t('Common.NoResults', 'Nincs találat.')}
        </Typography>
      ) : viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredMasters.map((master) => (
            <Grid item xs={12} sm={6} md={4} key={master.id}>
              <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
                {master.is_urgent && (
                  <Chip 
                    label={t('Business.Urgent', 'SÜRGŐS')} 
                    color="error" 
                    size="small" 
                    sx={{ position: 'absolute', top: -10, right: 10, zIndex: 1, fontWeight: 'bold' }} 
                  />
                )}
                <CardActionArea onClick={() => navigate(`/${i18n.language}/mester/${master.id}`)}>
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar 
                      src={master.picture || defaultProfilePicture} 
                      sx={{ width: 100, height: 100, mb: 2, border: '2px solid #105b79' }}
                    />
                    <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                      {master.name}
                    </Typography>
                    
                    {(master.category || master.description) && (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ fontWeight: 'bold', color: '#105b79', mt: 0.5 }}>
                        {master.category ? (master.category.startsWith('Business.') ? t(master.category) : master.category) : 
                         (master.description && master.description.length < 50 ? master.description : '')}
                      </Typography>
                    )}
                    {/* Debug label if category is empty but we want to know why */}
                    {!master.category && (
                      <Typography variant="caption" sx={{ display: 'none' }}>
                        DEBUG: No category for {master.name}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {master.hourly_rate && (
                        <Chip label={`${master.hourly_rate} RON/óra`} size="small" variant="outlined" />
                      )}
                      {master.brings_tools && (
                        <Chip icon={<ConstructionIcon />} label={t('Business.ToolsIncluded', 'Szerszámmal')} size="small" variant="outlined" />
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                      {master.description ? master.description.substring(0, 100) + '...' : ''}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ borderRadius: 2 }}>
          <List disablePadding>
            {filteredMasters.map((master, index) => (
              <React.Fragment key={master.id}>
                <CardActionArea onClick={() => navigate(`/${i18n.language}/mester/${master.id}`)}>
                  <ListItem sx={{ py: 2 }}>
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Avatar 
                        src={master.picture || defaultProfilePicture} 
                        sx={{ width: 60, height: 60, border: '1px solid #105b79' }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                              {master.name}
                            </Typography>
                            {(master.category || master.description) && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                {master.category ? (master.category.startsWith('Business.') ? t(master.category) : master.category) : 
                                 (master.description && master.description.length < 100 ? master.description : '')}
                              </Typography>
                            )}
                          </Box>
                          {master.is_urgent && (
                            <Chip label={t('Business.Urgent', 'SÜRGŐS')} color="error" size="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '600px' }}>
                            {master.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            {master.hourly_rate && (
                              <Chip label={`${master.hourly_rate} RON/óra`} size="small" variant="outlined" />
                            )}
                            {master.brings_tools && (
                              <Chip icon={<ConstructionIcon />} label={t('Business.ToolsIncluded', 'Szerszámmal')} size="small" variant="outlined" />
                            )}
                            {master.szek && (
                              <Chip label={master.szek} size="small" />
                            )}
                            {master.telepules && (
                              <Chip label={master.telepules} size="small" variant="outlined" />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </CardActionArea>
                {index < filteredMasters.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}
    </Container>
  );
}

export default Masters;